import { TFunction } from "i18next";

import { MailMergeAccessState, MailMergeWriteFailure } from "./MailMergeAccess.types";

interface AccessStateInput {
  isSupported: boolean;
  expectedFolderLeaf: string | null;
  handle: FileSystemDirectoryHandle | null;
  permission: PermissionState | null;
}

/**
 * Compares names the way Windows does — by uppercasing — so `Mergedata` and `MergeData` are one
 * folder here as they are to Explorer and Word.
 *
 * Deliberately not locale collation: that also folds distinctions Windows keeps (`Grosskunden` vs
 * `Großkunden`), and writing into a folder the templates never read is the failure the mismatch
 * state exists to prevent. Comparing the original lengths is what rejects those, since `ß`
 * uppercases to `SS`.
 */
export function folderNamesMatch(granted: string, expected: string): boolean {
  return granted.length === expected.length && granted.toUpperCase() === expected.toUpperCase();
}

/**
 * The folder a configured path ends in. The setting holds a whole path — `C:\ExportEx\`, the way the
 * Word templates refer to it — but a released folder reports nothing but its own name back, so the
 * leaf is the only part a grant can be matched against.
 */
export function folderLeafOf(folderPath: string): string | null {
  const segments = folderPath.split(/[\\/]/).filter((segment) => segment !== "");
  return segments[segments.length - 1] ?? null;
}

/**
 * The single definition of where a grant stands. Kept pure and separate because the rendered state,
 * the outcome of a restore attempt and the check made just before a write all have to answer the
 * same question the same way.
 *
 * Only the folder's leaf name can be read back from a handle, never its path, so a folder of the
 * right name in the wrong place passes — an accepted residual risk of the browser's security model.
 *
 * The order matters: a missing folder leaf outranks a stored handle, because a grant can neither be
 * checked nor usefully replaced while there is no name to check it against.
 */
export function resolveAccessState({
  isSupported,
  expectedFolderLeaf,
  handle,
  permission,
}: AccessStateInput): MailMergeAccessState {
  if (!isSupported) {
    return MailMergeAccessState.Unsupported;
  }

  if (!expectedFolderLeaf) {
    return MailMergeAccessState.NotConfigured;
  }

  if (!handle) {
    return MailMergeAccessState.NotGranted;
  }

  if (permission === "denied") {
    return MailMergeAccessState.PermissionDenied;
  }

  if (permission !== "granted") {
    return MailMergeAccessState.PermissionLost;
  }

  if (!folderNamesMatch(handle.name, expectedFolderLeaf)) {
    return MailMergeAccessState.FolderMismatch;
  }

  return MailMergeAccessState.Ready;
}

/** What every mail merge function tells the user about a failed write, so all of them name the same cause. */
export function writeFailureMessage(failure: MailMergeWriteFailure, t: TFunction): string {
  switch (failure) {
    case MailMergeWriteFailure.NotReady:
      return t("mailMerge.access.errors.notReady");
    case MailMergeWriteFailure.PermissionLost:
      return t("mailMerge.access.errors.permissionLost");
    case MailMergeWriteFailure.FileLocked:
      return t("mailMerge.access.errors.fileLocked");
    case MailMergeWriteFailure.WriteFailed:
      return t("mailMerge.access.errors.writeFailed");
  }
}
